import { useEffect, useState, type FormEvent } from 'react';
import { useSession } from '../App';
import type { Identity } from '../api';
import { Card, ConfirmDialog, ErrorNotice, Loading, Notice, PageHeader, useAsync } from '../components/ui';
import { formatDate } from '../format';

const LANGUAGES = [
  { value: '', label: 'Automatic (browser language)' },
  { value: 'en', label: 'English' },
  { value: 'pl', label: 'Polski' },
  { value: 'de', label: 'Deutsch' },
];

const PROVIDER_LABELS: Record<string, string> = {
  email: 'Email & password',
  google: 'Google',
  apple: 'Apple',
  facebook: 'Facebook',
  microsoft: 'Microsoft',
  passkey: 'Passkey',
  other: 'Other',
};

export function ProfilePage() {
  const { client, claims } = useSession();
  const state = useAsync(() => Promise.all([client.me(), client.identities()]), [client]);
  const [displayName, setDisplayName] = useState('');
  const [avatarUrl, setAvatarUrl] = useState('');
  const [locale, setLocale] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [unlinking, setUnlinking] = useState<Identity | null>(null);
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [unlinkError, setUnlinkError] = useState<unknown>(null);

  useEffect(() => {
    if (!state.data) return;
    const [me] = state.data;
    setDisplayName(me.displayName ?? '');
    setAvatarUrl(me.avatarUrl ?? '');
    setLocale(me.locale ?? '');
  }, [state.data]);

  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <ErrorNotice error={state.error} onRetry={() => void state.reload()} />;
  const [me, { identities }] = state.data!;

  const email = me.email ?? (typeof claims?.email === 'string' ? claims.email : null);
  const dirty =
    displayName.trim() !== (me.displayName ?? '') ||
    avatarUrl.trim() !== (me.avatarUrl ?? '') ||
    locale !== (me.locale ?? '');
  const avatarValid = !avatarUrl.trim() || /^https:\/\/\S+$/i.test(avatarUrl.trim());

  async function save(event: FormEvent) {
    event.preventDefault();
    setSaving(true);
    setSaved(false);
    setSaveError(null);
    try {
      await client.updateMe({
        displayName: displayName.trim() || undefined,
        avatarUrl: avatarUrl.trim() || undefined,
        locale: locale || undefined,
      });
      await state.reload();
      setSaved(true);
    } catch (error) {
      setSaveError(error);
    } finally {
      setSaving(false);
    }
  }

  async function unlink() {
    if (!unlinking) return;
    setUnlinkBusy(true);
    setUnlinkError(null);
    try {
      await client.unlinkIdentity(unlinking.id);
      setUnlinking(null);
      await state.reload();
    } catch (error) {
      setUnlinkError(error);
      setUnlinking(null);
    } finally {
      setUnlinkBusy(false);
    }
  }

  const preview = avatarValid && avatarUrl.trim() ? avatarUrl.trim() : typeof claims?.picture === 'string' ? claims.picture : null;

  return (
    <>
      <PageHeader eyebrow="Profile" title="Account details" description="How you appear in DeskTouch, and how you sign in." />

      <div className="grid two">
        <Card title="Profile">
          <form className="form" onSubmit={save}>
            <div className="avatar-row">
              {preview ? (
                <img className="avatar" src={preview} alt="" referrerPolicy="no-referrer" />
              ) : (
                <span className="avatar placeholder">{(displayName || email || '?').charAt(0).toUpperCase()}</span>
              )}
              <div>
                <strong>{displayName || email || 'Your name'}</strong>
                <p className="fine">{email ?? 'No email on file'}</p>
              </div>
            </div>

            <label>
              <span>Display name</span>
              <input value={displayName} onChange={(event) => setDisplayName(event.target.value)} maxLength={120} placeholder="How DeskTouch should call you" />
            </label>

            <label>
              <span>Email</span>
              <input value={email ?? ''} readOnly placeholder="Managed by your sign-in method" />
              <small>Your email comes from how you sign in, so it is changed there rather than here.</small>
            </label>

            <label>
              <span>Avatar image URL</span>
              <input
                type="url"
                value={avatarUrl}
                onChange={(event) => setAvatarUrl(event.target.value)}
                placeholder="https://…"
                aria-invalid={!avatarValid}
              />
              {!avatarValid && <small className="error-text">Use an https:// address.</small>}
            </label>

            <label>
              <span>Language</span>
              <select value={locale} onChange={(event) => setLocale(event.target.value)}>
                {/* The API can set a language but not unset one, so "automatic" is only offered until one is chosen. */}
                {LANGUAGES.filter((language) => language.value || !me.locale).map((language) => (
                  <option key={language.value} value={language.value}>
                    {language.label}
                  </option>
                ))}
              </select>
              <small>Used for emails and the payment page.</small>
            </label>

            <ErrorNotice error={saveError} />
            {saved && !dirty && <Notice tone="success">Profile saved.</Notice>}
            <div className="form-actions">
              <button type="submit" className="primary" disabled={saving || !dirty || !avatarValid}>
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </form>
        </Card>

        <div className="stack">
          <Card title="Sign-in methods">
            <ErrorNotice error={unlinkError} />
            {identities.length === 0 ? (
              <p className="muted">
                You sign in through DeskTouch's sign-in page
                {email ? ` as ${email}` : ''}. Linked methods appear here once your profile has been synchronised.
              </p>
            ) : (
              <ul className="list">
                {identities.map((identity) => (
                  <li key={identity.id}>
                    <div>
                      <strong>{PROVIDER_LABELS[identity.provider] ?? identity.provider}</strong>
                      <span className="fine">
                        {identity.email ?? 'Linked account'} · since {formatDate(identity.linkedAt)}
                      </span>
                    </div>
                    {identities.length > 1 ? (
                      <button type="button" className="ghost small" onClick={() => setUnlinking(identity)}>
                        Unlink
                      </button>
                    ) : (
                      <span className="badge neutral">Only method</span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </Card>

          <Card title="Account">
            <dl className="facts">
              <dt>Account ID</dt>
              <dd>
                <code>{me.id}</code>
              </dd>
              <dt>Created</dt>
              <dd>{formatDate(me.createdAt)}</dd>
              <dt>Status</dt>
              <dd>{me.status === 'active' ? 'Active' : me.status}</dd>
            </dl>
          </Card>
        </div>
      </div>

      <ConfirmDialog
        open={unlinking !== null}
        title="Unlink this sign-in method?"
        confirmLabel="Unlink"
        tone="danger"
        busy={unlinkBusy}
        onCancel={() => setUnlinking(null)}
        onConfirm={() => void unlink()}
      >
        <p>
          You will no longer be able to sign in with {unlinking ? PROVIDER_LABELS[unlinking.provider] ?? unlinking.provider : ''}. Your other
          methods keep working.
        </p>
      </ConfirmDialog>
    </>
  );
}
